import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router-dom";
import type {
  DealPipelineStageMetric,
  MetricsFilter,
} from "@/api/dealFlow/metrics";

// Shared filter state for the metrics dashboard.
// The filter is URL-synced via useSearchParams so a sub-tab change keeps
// the operator's selection, and the resulting URL is bookmarkable.

interface MetricsFilterContextValue {
  filter: MetricsFilter;
  setFilter: (next: MetricsFilter) => void;
  patch: (delta: Partial<MetricsFilter>) => void;
  reset: () => void;
}

const Ctx = createContext<MetricsFilterContextValue | undefined>(undefined);

const FILTER_KEYS = ["from", "to", "stage", "broker", "analyst"] as const;

function readFilter(params: URLSearchParams): MetricsFilter {
  const f: MetricsFilter = {};
  const from = params.get("from");
  if (from) f.from = from;
  const to = params.get("to");
  if (to) f.to = to;
  const stage = params.get("stage");
  if (stage) f.stage = stage as DealPipelineStageMetric;
  const broker = params.get("broker");
  if (broker) f.broker = broker;
  const analyst = params.get("analyst");
  if (analyst) f.analyst = analyst;
  return f;
}

export function MetricsFilterProvider({ children }: { children: ReactNode }) {
  const [params, setParams] = useSearchParams();
  const filter = useMemo(() => readFilter(params), [params]);

  const setFilter = useCallback(
    (next: MetricsFilter) => {
      setParams(
        (current) => {
          const out = new URLSearchParams(current);
          for (const key of FILTER_KEYS) out.delete(key);
          if (next.from) out.set("from", next.from);
          if (next.to) out.set("to", next.to);
          if (next.stage) out.set("stage", next.stage);
          if (next.broker) out.set("broker", next.broker);
          if (next.analyst) out.set("analyst", next.analyst);
          return out;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const patch = useCallback(
    (delta: Partial<MetricsFilter>) => setFilter({ ...filter, ...delta }),
    [filter, setFilter],
  );

  const reset = useCallback(() => setFilter({}), [setFilter]);

  const value = useMemo<MetricsFilterContextValue>(
    () => ({ filter, setFilter, patch, reset }),
    [filter, setFilter, patch, reset],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useMetricsFilter(): MetricsFilterContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error("useMetricsFilter must be used inside <MetricsFilterProvider>.");
  }
  return ctx;
}
